import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { getShare } from "../lib/api";
import { MODELS } from "../lib/models";
import { Markdown } from "../components/Markdown";
import { AgreementMeter } from "../components/AgreementMeter";
import { FullScreenLoader } from "../components/FullScreenLoader";
import { KidFriendlyBadge } from "../components/KidFriendlyBadge";

export default function SharePage() {
  const { shareId } = useParams();
  const [share, setShare] = useState(null);

  useEffect(() => {
    getShare(shareId).then(setShare).catch(() => setShare(false));
  }, [shareId]);

  if (share === null) return <FullScreenLoader />;
  const merger = share && MODELS.find((m) => m.key === share.merger);

  return (
    <div className="what-app min-h-screen overflow-y-auto">
      <header className="max-w-3xl mx-auto px-6 sm:px-10 pt-10 flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-2" data-testid="share-home-link">
          <span className="font-display text-2xl font-extrabold tracking-tight text-white">WHAT?</span>
          <span className="what-dot" />
        </Link>
        <Link to="/" data-testid="share-cta-btn" className="what-btn-primary !py-2 text-sm">
          Ask WHAT? yourself <ArrowRight size={15} />
        </Link>
      </header>
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16 what-rise">
        {share === false ? (
          <div data-testid="share-not-found">
            <h1 className="font-display text-3xl font-bold text-white">This shared answer doesn't exist</h1>
            <p className="text-slate-400 mt-3">The link may be wrong, or the owner deleted the chat.</p>
          </div>
        ) : (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-slate-500 mb-4" data-testid="share-author">
              Shared by {share.author}
            </p>
            <h1 data-testid="share-question" className="font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight leading-snug">
              {share.question}
            </h1>
            <div className="what-answer mt-8" data-testid="share-answer-container">
              <div className="flex items-center gap-2 mb-4 text-xs font-mono uppercase tracking-[0.16em] text-[#D4FF3A]">
                <Sparkles size={13} /> {share.debated && !share.agreement ? "Debate verdict" : "Combined best answer"}
              </div>
              <Markdown testid="share-answer-markdown">{share.final}</Markdown>
              {share.agreement && <AgreementMeter agreement={share.agreement} after={share.agreement_after} />}
              {share.debated && (
                <p data-testid="share-debated-note" className="mt-3 text-xs font-mono text-slate-500">{share.agreement ? "The AIs disagreed and debated before this answer was written." : "This answer came out of a debate with the AIs."}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-6 text-xs text-slate-500 font-mono">
              Sources:
              {MODELS.filter((m) => share.models.includes(m.key)).map((m) => (
                <span key={m.key} className="what-chip !text-xs" style={{ "--c": m.color }}>{m.name} {m.version}</span>
              ))}
              {share.models.length > 1 && merger && <span className="ml-1">· combined by {merger.name}</span>}
            </div>
          </>
        )}
        <div className="mt-12"><KidFriendlyBadge testid="share-kid-friendly-badge" /></div>
      </main>
    </div>
  );
}
